import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SatelliteAnnualNdviAnalysisData, SatelliteViewModel } from '@/app/proxy/proxies';
import { EChartsOption } from 'echarts';
import { SharedModule } from '@/app/shared/shared.module';
import * as echarts from 'echarts/core';
import { VisualMapComponent } from 'echarts/components';
import { PlantationService } from '@/app/core/services/plantation.service';
import { NoAnalysisComponent } from '../no-analysis/no-analysis.component';
import { LuxonFormatPipe } from '@/app/shared/pipes/luxon-format.pipe';
import { DateTime } from 'luxon';

// Register components
echarts.use([VisualMapComponent]);

@Component({
  selector: 'app-vegetation-health',
  imports: [SharedModule, NoAnalysisComponent],
  templateUrl: './vegetation-health.component.html',
  styleUrl: './vegetation-health.component.less',
  providers: [TranslateService, LuxonFormatPipe],
})
export class VegetationHealthComponent implements OnInit, OnChanges {
  private luxonFormatPipe = inject(LuxonFormatPipe);

  @Input() loading = true;
  @Input() data: SatelliteAnnualNdviAnalysisData[] = [];
  @Input() selectedDay: SatelliteViewModel;
  lastValue: SatelliteAnnualNdviAnalysisData;
  chartOption!: EChartsOption;

  constructor(private translateService: TranslateService, public plantationService: PlantationService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['data']) {
      this.lastValue = null;
      this.makeChart();
    }
  }

  ngOnInit(): void {
  }

  private getValidItems(): SatelliteAnnualNdviAnalysisData[] {
    return this.data.filter((item) => !item.isCloudy);
  }

  makeChart() {
    if (!(this.data?.length > 0)) {
      this.chartOption = null;
      return;
    }

    const validItems = this.getValidItems();
    this.lastValue = validItems.length > 0
      ? validItems[validItems.length - 1]
      : this.data[this.data.length - 1];

    const min = validItems.length > 0
      ? +Math.min(...validItems.map((item) => item.ndviMinValue)).toFixed(2)
      : 0;
    const max = validItems.length > 0
      ? +Math.max(...validItems.map((item) => item.ndviMaxValue)).toFixed(2)
      : 0;

    const startDate = this.data[0].date.toJSDate();
    const endDate = this.data[this.data.length - 1].date.toJSDate();

    // Generate tick positions (1st day of each month between start and end)
    const ticks: Date[] = [];
    const current = new Date(startDate);
    current.setDate(1);

    while (current <= endDate) {
      ticks.push(new Date(current));
      current.setMonth(current.getMonth() + 1);
    }

    this.chartOption = {
      title: { show: false },
      grid: {
        left: '50px',
        right: '50px',
        top: '10px',
        bottom: '50px',
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#f0f0f0',
            borderColor: '#bbb',
            borderWidth: 1,
            color: '#000',
          },
        },
        formatter: (params: any) => {
          const index = params[0].dataIndex;
          const item = this.data[index];
          const date = this.luxonFormatPipe.transform(
            item.date,
            'yyyy-MM-dd'
          );

          if (item.isCloudy) {
            const cloudyTitle = this.translateService.instant('doktar_app.services.crop_monitoring.cloudy_weather');

            return `<b>${date}</b><br/>☁️ ${cloudyTitle}`;
          }

          const minVal = item.ndviMinValue;
          const maxVal = item.ndviMaxValue;
          const avgVal = item.ndviAverangeValue;

          return `
          <b>${date}</b><br/>
          ${this.translateService.instant('doktar_app.common.min')}: ${minVal.toFixed(2)}<br/>
          ${this.translateService.instant('doktar_app.common.max')}: ${maxVal.toFixed(2)}<br/>
          ${this.translateService.instant('doktar_app.common.average')}: ${avgVal.toFixed(2)}
        `;
        },
      },
      xAxis: {
        type: 'time',
        min: startDate,
        max: endDate,
        axisLabel: {
          formatter: (value: number) => {
            const date = new Date(value);
            const luxonDate = DateTime.fromJSDate(date);
            return this.luxonFormatPipe.transform(
              luxonDate,
              'LLL',
            );
          },
        },
        axisTick: {
          show: true,
        },
        splitLine: { show: false },
        splitNumber: ticks.length,
      },
      // 👇 inject custom tick positions
      xAxisTick: {
        show: true,
        interval: (index: number, value: any) => ticks.includes(value),
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisLabel: { color: '#444' },
        splitLine: {
          lineStyle: { color: '#e0e0e0', type: 'dashed' },
        },
        splitNumber: 4,
        min,
        max,
      },
      visualMap: {
        show: false,
        type: 'continuous',
        dimension: 1,
        min,
        max,
        inRange: {
          color: ['#e67347', '#F6E44F', '#68AB43'],
        },
      },
      series: [
        {
          name: this.translateService.instant('doktar_app.common.min'),
          type: 'line',
          data: this.data.map((item) => [
            item.date.toJSDate(),
            item.isCloudy ? null : item.ndviMinValue,
          ]),
          lineStyle: { opacity: 0 },
          stack: 'confidence-band',
          symbol: 'none',
          connectNulls: true,
        },
        {
          name: this.translateService.instant('doktar_app.common.max'),
          type: 'line',
          data: this.data.map((item) => [
            item.date.toJSDate(),
            item.isCloudy ? null : item.ndviMaxValue,
          ]),
          lineStyle: { opacity: 0 },
          areaStyle: { color: '#deeff5' },
          stack: 'confidence-band',
          symbol: 'none',
          connectNulls: true,
        },
        {
          name: this.translateService.instant('doktar_app.common.average'),
          type: 'line',
          data: this.data.map((item) => [
            item.date.toJSDate(),
            item.isCloudy ? null : item.ndviAverangeValue,
          ]),
          lineStyle: {
            width: 3,
          },
          showSymbol: false,
          connectNulls: true,
        },
      ],
    };
  }
}
